<template>
  <div class="phone-input-container">
    <div class="flex">
      <!-- Country selector -->
      <Dropdown 
        v-model="selectedCountry" 
        :options="countries" 
        optionLabel="name"
        :filter="true"
        filterBy="name,dialCode"
        placeholder="Pays"
        class="country-dropdown"
        @change="onCountryChange"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center">
            <img :src="slotProps.value.flag" :alt="slotProps.value.name" class="w-6 h-4 mr-2" />
            <span>{{ slotProps.value.dialCode }}</span>
          </div>
          <span v-else>{{ slotProps.placeholder }}</span>
        </template>
        <template #option="slotProps">
          <div class="flex items-center">
            <img :src="slotProps.option.flag" :alt="slotProps.option.name" class="w-6 h-4 mr-2" />
            <span class="mr-2">{{ slotProps.option.name }}</span>
            <span class="text-gray-500">{{ slotProps.option.dialCode }}</span>
          </div>
        </template>
      </Dropdown>
      
      <!-- Phone number input -->
      <InputText 
        v-model="phoneNumber"
        placeholder="Numéro de téléphone"
        class="phone-number-input flex-1 ml-2"
        @input="onPhoneInput"
      />
    </div>
    
    <!-- Full phone number display -->
    <div v-if="fullPhoneNumber" class="mt-2 text-sm text-gray-600">
      Numéro complet: {{ fullPhoneNumber }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineProps, defineEmits } from 'vue';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import countryPhoneCodes from '@/assets/country_phone_codes.js';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Numéro de téléphone'
  }
});

const emit = defineEmits(['update:modelValue']);

const countries = ref(countryPhoneCodes);
const selectedCountry = ref(null);
const phoneNumber = ref('');

// Find France as default country
const defaultCountry = countries.value.find(country => country.isoCode === 'FR') || countries.value[0];

const fullPhoneNumber = computed(() => {
  if (selectedCountry.value && phoneNumber.value) {
    // Remove spaces from phone number but keep leading zeros
    const cleanNumber = phoneNumber.value.replace(/\s+/g, '');
    return `${selectedCountry.value.dialCode}${cleanNumber}`;
  }
  return '';
});

const onCountryChange = () => {
  updateModelValue();
};

const onPhoneInput = () => {
  updateModelValue();
};

const updateModelValue = () => {
  emit('update:modelValue', fullPhoneNumber.value);
};

// Parse existing phone number when component mounts or modelValue changes
const parsePhoneNumber = (phoneValue) => {
  if (!phoneValue) {
    selectedCountry.value = defaultCountry;
    phoneNumber.value = '';
    return;
  }
  
  // Find matching country by dial code
  const matchingCountry = countries.value.find(country => 
    phoneValue.startsWith(country.dialCode)
  );
  
  if (matchingCountry) {
    selectedCountry.value = matchingCountry;
    phoneNumber.value = phoneValue.substring(matchingCountry.dialCode.length);
  } else {
    selectedCountry.value = defaultCountry;
    phoneNumber.value = phoneValue;
  }
};

watch(() => props.modelValue, (newValue) => {
  parsePhoneNumber(newValue);
}, { immediate: true });

onMounted(() => {
  if (!selectedCountry.value) {
    selectedCountry.value = defaultCountry;
  }
});
</script>

<style scoped>
.phone-input-container {
  width: 100%;
}

.country-dropdown {
  min-width: 120px;
}

.phone-number-input {
  flex: 1;
}

:deep(.p-dropdown-panel) {
  max-height: 200px;
}
</style>